import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { DashboardData, EDashboard } from './types';
import { MainDashboard } from './views/MainDashboard';
import { EGDashboard } from './views/EGDashboard';
import { DIDashboard } from './views/DIDashboard';
import { CodeDashboard } from './views/CodeDashboard';
import { DashboardNavPanel } from './components/dashboard-nav-panel';
import { Job } from '../../types';
import { ChevronRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Indicator } from '../../components/Indicator';
import { urlEnum, useUrlState } from '../../hooks/useUrlState';
import { useBackTo } from '../../hooks/useBackTo';

interface DashboardViewProps {
  sharedData?: DashboardData;
}

const URL_SPEC = {
  view: urlEnum(['', 'main', 'di', 'eg', 'code'] as const, ''),
};

const DASHBOARD_ORDER = [EDashboard.MAIN, EDashboard.DI, EDashboard.EG, EDashboard.CODE];

export const DashboardView: React.FC<DashboardViewProps> = ({ sharedData }) => {
  const isSharedMode = !!sharedData;
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const backHref = useBackTo(`/jobs/${jobId}`);
  const [data, setData] = useState<DashboardData | null>(sharedData || null);
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(!isSharedMode);
  const [error, setError] = useState<string | null>(null);
  const [state, set] = useUrlState(URL_SPEC);

  useEffect(() => {
    if (!isSharedMode && jobId) {
      fetchDashboardData(jobId);
    }
  }, [jobId, isSharedMode, sharedData]);

  const fetchDashboardData = async (jobId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both job details and dashboard data
      const [jobData, dashboardData] = await Promise.all([
        apiService.getJob(jobId),
        apiService.getDashboardData(jobId)
      ]);
      
      setJob(jobData);
      setData(dashboardData);
      
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <Indicator />
          <p className="text-deep-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-16">
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 max-w-md">
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-16">
          <p className="text-deep-gray-400">No dashboard data.</p>
        </div>
      </div>
    );
  }

  // Determine available dashboards
  const availableDashboards = {
    main: !!data.main,
    eg: !!data.eg,
    di: !!data.di,
    code: !!data.code,
  };

  const requestedDashboard = state.view as EDashboard | '';
  const currentDashboard = requestedDashboard && availableDashboards[requestedDashboard]
    ? requestedDashboard
    : DASHBOARD_ORDER.find((dashboard) => availableDashboards[dashboard]) ?? EDashboard.MAIN;

  const handleDashboardChange = (dashboardId: EDashboard) => {
    set({ view: dashboardId });
  };

  const renderDashboard = () => {
    switch (currentDashboard) {
      case EDashboard.MAIN:
        return <MainDashboard data={data.main} />;
      case EDashboard.CODE:
        return data.code ? <CodeDashboard data={data.code} /> : <div className="text-deep-gray-400">Code dashboard not available.</div>;
      case EDashboard.EG:
        return data.eg ? <EGDashboard data={data.eg} /> : <div className="text-deep-gray-400">EG dashboard not available.</div>;
      case EDashboard.DI:
        return data.di ? <DIDashboard data={data.di} /> : <div className="text-deep-gray-400">DI dashboard not available.</div>;
      default:
        return <div className="text-deep-gray-400">Select a dashboard.</div>;
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="space-y-6">
        {/* Breadcrumbs and Back Navigation - Only show in non-shared mode */}
        {!isSharedMode && (
          <div className="bg-deep-gray-800 rounded-lg border border-deep-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm">
                <Link to="/" className="text-deep-gray-400 hover:text-primary transition-colors">
                  Jobs
                </Link>
                <ChevronRightIcon className="h-4 w-4 text-deep-gray-400" />
                <Link to={backHref} className="text-deep-gray-400 hover:text-primary transition-colors">
                  {job?.project_name || `Job ${jobId}`}
                </Link>
                <ChevronRightIcon className="h-4 w-4 text-deep-gray-400" />
                <span className="text-slate-300 font-medium">Analysis Dashboard</span>
              </div>
              <button
                onClick={() => navigate(backHref)}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Job Details
              </button>
            </div>
          </div>
        )}

        {/* Dashboard Navigation Panel */}
        <div className="flex w-full flex-col gap-4">
          <DashboardNavPanel
            activeDashboard={currentDashboard}
            onDashboardChange={handleDashboardChange}
            availableDashboards={availableDashboards}
            projectName={isSharedMode ? "shared" : job?.project_name}
            jobId={jobId}
          />

          {/* Dashboard Content */}
          <div id="dashboard-content" className="w-full">
            {renderDashboard()}
          </div>
        </div>
      </div>
    </div>
  );
};
